import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Mail, Pencil, Printer, Download } from 'lucide-react';
import { vendorInvoiceApi } from '../../services/vendorInvoiceService';
import { useVendorProfile } from '../../services/vendorProfileService';
import { getVendorStatus } from '../../services/subscriptionService';
import InvoiceLivePreview from '../../components/vendor/invoice/InvoiceLivePreview';
import Button from '../../components/shared/Button';
import { downloadInvoicePdfFromElement } from '../../utils/vendorInvoicePdf';
import { canForwardInvoiceByEmail } from '../../utils/invoiceFormat';
import { useToastContext } from '../../contexts/ToastContext';

const VendorInvoiceDetail = () => {
  const { id } = useParams();
  const { data: profileResp } = useVendorProfile();
  const { success: showSuccess, error: showError } = useToastContext();
  const invoicePrintRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    document.body.classList.add('invoice-print-root');
    return () => document.body.classList.remove('invoice-print-root');
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-invoice', id],
    queryFn: () => vendorInvoiceApi.getOne(id),
    enabled: !!id,
  });

  const { data: vendorSubRaw } = useQuery({
    queryKey: ['vendor-subscription-status'],
    queryFn: async () => {
      const r = await getVendorStatus();
      return r?.data ?? r;
    },
    staleTime: 60 * 1000,
  });

  const invoice = data?.data;
  const canForwardEmail = canForwardInvoiceByEmail(vendorSubRaw);
  const vp = profileResp?.data ?? {};

  const vendorLabel = vp.business_name || 'Your business';
  const vendorAddressLines = [
    vp.address,
    [vp.city, vp.state, vp.zip_code].filter(Boolean).join(', '),
    vp.country,
    vp.business_phone,
  ].filter(Boolean);

  const previewLines = useMemo(() => {
    if (!invoice) return [];
    if (invoice.line_items?.length) {
      return invoice.line_items.map((row) => ({
        description: row.description,
        quantity: Number(row.quantity),
        unit_price: Number(row.unit_price),
      }));
    }
    return [
      {
        description: 'Invoice items',
        quantity: 1,
        unit_price: Number(invoice.subtotal ?? invoice.total_amount ?? 0),
      },
    ];
  }, [invoice]);

  const formatDisplayDate = (val) => {
    if (!val) return '';
    const s = typeof val === 'string' ? val.slice(0, 10) : val;
    try {
      return new Date(s + 'T12:00:00').toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return String(val);
    }
  };

  const bookingRef = invoice?.booking?.booking_reference || invoice?.booking?.id?.slice(0, 8);

  const canEdit =
    invoice && !['paid', 'cancelled'].includes(String(invoice.status || '').toLowerCase());

  const handlePrint = () => window.print();

  const handleDownload = async () => {
    if (!invoice || !invoicePrintRef.current) return;
    setPdfLoading(true);
    try {
      const name = `invoice-${invoice.invoice_number || invoice.id || 'export'}.pdf`;
      await downloadInvoicePdfFromElement(invoicePrintRef.current, name);
    } catch (e) {
      console.error(e);
    } finally {
      setPdfLoading(false);
    }
  };

  const openEmailModal = () => {
    const c = invoice?.client || {};
    setEmailTo(c.email || '');
    setEmailModalOpen(true);
  };

  const handleSendInvoiceEmail = async () => {
    if (!id || !emailTo.trim()) {
      showError('Missing email', 'Enter a valid recipient email address.');
      return;
    }
    setEmailSending(true);
    try {
      const res = await vendorInvoiceApi.sendEmail(id, { email: emailTo.trim() });
      if (res?.success) {
        showSuccess('Email sent', res.message || 'Invoice emailed to the client.');
        setEmailModalOpen(false);
      } else {
        showError('Could not send', res?.message || 'Try again.');
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Could not send invoice email.';
      showError('Could not send', msg);
    } finally {
      setEmailSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-10 h-10 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-700 mb-4">Invoice not found.</p>
        <Link to="/vendor/invoices" className="text-primary-600 hover:underline">
          Back to invoices
        </Link>
      </div>
    );
  }

  const client = invoice.client || {};
  const taxRateNum = invoice.tax_rate != null ? Number(invoice.tax_rate) : 0;

  return (
    <>
      <div className="inv-detail-no-print space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1">
            <Link
              to="/vendor/invoices"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-3"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to invoices
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">
              Invoice #{invoice.invoice_number || invoice.id}
            </h1>
            <p className="text-sm text-gray-600 mt-1">{client.name}</p>
          </div>
          {/* One tight row: shrink-wrapped so controls stay grouped (no stray gap before Forward) */}
          <div className="flex shrink-0 flex-col items-stretch sm:items-end">
            <div className="inline-flex max-w-full flex-wrap items-center justify-start gap-1.5 sm:justify-end">
              {canEdit && (
                <Link
                  to={`/vendor/invoices/${id}/edit`}
                  className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <Pencil className="h-3.5 w-3.5 shrink-0" />
                  Edit
                </Link>
              )}
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={handlePrint}
                icon={Printer}
                className="!h-7 shrink-0 !py-0"
              >
                Print
              </Button>
              <Button
                type="button"
                size="xs"
                onClick={handleDownload}
                icon={Download}
                disabled={pdfLoading}
                className="!h-7 shrink-0 !py-0"
              >
                {pdfLoading ? 'Preparing…' : 'Download'}
              </Button>
              {canForwardEmail ? (
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  onClick={openEmailModal}
                  icon={Mail}
                  className="!h-7 shrink-0 !py-0"
                >
                  Forward to email
                </Button>
              ) : (
                <div className="inline-flex max-w-full flex-col items-end gap-0.5">
                  <button
                    type="button"
                    disabled
                    title="Available on paid plans"
                    className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 text-xs font-medium text-gray-400 cursor-not-allowed"
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0" />
                    Forward to email
                  </button>
                  <Link
                    to="/vendor/plan"
                    className="max-w-[12rem] text-right text-[11px] leading-snug text-primary-600 hover:underline sm:max-w-none"
                  >
                    Upgrade plan to email invoices
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        {!canEdit && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
            This invoice is {invoice.status}. Editing is disabled.
          </p>
        )}
      </div>

      <div
        id="invoice-print-area"
        ref={invoicePrintRef}
        className="max-w-3xl mx-auto mt-4"
      >
        <InvoiceLivePreview
          vendorLabel={vendorLabel}
          vendorAddressLines={vendorAddressLines}
          billToName={client.name}
          billToEmail={client.email}
          billToPhone={client.phone}
          invoiceNumberPreview={invoice.invoice_number || invoice.id}
          invoiceDate={formatDisplayDate(invoice.invoice_date)}
          dueDate={formatDisplayDate(invoice.due_date)}
          lineItems={previewLines}
          taxRate={taxRateNum}
          subtotal={Number(invoice.subtotal ?? 0)}
          taxAmount={Number(invoice.tax_amount ?? 0)}
          total={Number(invoice.total_amount ?? 0)}
          notes={invoice.notes}
          paymentTerms={invoice.payment_terms}
          bookingRef={bookingRef}
          statusLabel={
            invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : ''
          }
        />
      </div>

      {emailModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 inv-detail-no-print"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invoice-email-title"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4 border border-gray-200">
            <h2 id="invoice-email-title" className="text-lg font-semibold text-gray-900">
              Forward invoice by email
            </h2>
            <p className="text-sm text-gray-600">
              Send a copy of this invoice to your client or another address.
            </p>
            <div>
              <label htmlFor="invoice-email-to" className="block text-sm font-medium text-gray-700 mb-1">
                Recipient email
              </label>
              <input
                id="invoice-email-to"
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="client@example.com"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEmailModalOpen(false)}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <Button
                type="button"
                size="sm"
                onClick={handleSendInvoiceEmail}
                disabled={emailSending || !emailTo.trim()}
                icon={Mail}
              >
                {emailSending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VendorInvoiceDetail;
